<template>
  <div>
    <v-toolbar class="event-report-toolbar">
      <!-- desktop -->
      <v-autocomplete
          label="Veranstaltung"
          class="hidden-md-and-down"
          style="margin-bottom: -20px; margin-left: -15px"
          :items="eventItems"
          v-model="event"
          :placeholder="$t('choose_event')"
          item-text="name"
          item-value="id"
          return-object
          :loading="loading || !events.loaded"
          :disabled="loading"
          @input="loadReport(event.id)"
      >
        <template slot="selection" slot-scope="{ item }">
          <span>{{ $formatDate(item.start) + ' - ' + item.name }}</span>
        </template>
        <template slot="item" slot-scope="data">
          <span>{{ $formatDate(data.item.start) + ' - ' + data.item.name }}</span>
        </template>
      </v-autocomplete>

      <!-- mobile -->
      <v-select
          class="hidden-lg-and-up"
          label="Veranstaltung"
          style="margin-bottom: -20px; margin-left: -15px"
          :items="eventItems"
          v-model="event"
          :placeholder="$t('choose_event')"
          item-text="name"
          item-value="id"
          return-object
          :loading="loading || !events.loaded"
          :disabled="loading"
          @input="loadReport(event.id)"
      >
        <template slot="selection" slot-scope="{ item }">
          <span>{{ $formatDate(item.start) + ' - ' + item.name }}</span>
        </template>
        <template slot="item" slot-scope="data">
          <span>{{ $formatDate(data.item.start) + ' - ' + data.item.name }}</span>
        </template>
      </v-select>

      <v-checkbox
          v-model="showOldEvents"
          class="ml-2"
          hint="Vergangene Turniere"
          persistent-hint
      ></v-checkbox>

      <v-spacer></v-spacer>

      <v-toolbar-items class="hidden-sm-and-down" v-show="event !== null && result.groups.length > 0">
        <v-btn flat @click="stats(event.id)" :disabled="loading || loadingStats" :loading="loadingStats">{{
            $t('stats')
          }}
        </v-btn>
        <v-btn flat @click="mails(event.id)" :disabled="loading || loadingMails" :loading="loadingMails">
          {{ $t('event_mails') }}
        </v-btn>
        <v-btn flat @click="csv(event.id)" :disabled="loading || loadingCSV" :loading="loadingCSV">{{
            $t('csv')
          }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card v-if="event">
      <v-card-title class="hidden-xs-only">
        <h1>Meldestand</h1>
        <v-spacer></v-spacer>
        <v-text-field
            :disabled="loading"
            :loading="loadingSearch"
            append-icon="search"
            label="Filter"
            single-line
            hide-details
            clearable
            v-model="searchBuffer"
            v-if="!$vuetify.breakpoint.xs"
        ></v-text-field>
      </v-card-title>

      <v-data-table
          id="event-report-table"
          :headers="headers"
          :items="result.groups"
          :loading="loading"
          :search="search"
          :custom-filter="filter"
          hide-actions
          no-data-text=""
          item-key="name"
      >
        <template slot="headerCell" slot-scope="props">
          <span v-if="result[props.header.value] !== undefined">{{ result[props.header.value] }}</span><br>
          <span slot="activator">{{ props.header.text }}</span>
        </template>

        <template slot="items" slot-scope="props">
          <tr @click="props.item.expanded = !props.item.expanded" class="expandable stats">
            <td>
              <v-icon v-if="!props.item.expanded" style="margin-bottom: -4px;">add</v-icon>
              <v-icon v-else style="margin-bottom: -4px;">remove</v-icon>
              {{ $t(props.item.type_name) + (!props.item.confirmed ? ' (unbestätigt)' : '') }}
            </td>
            <td class="text-xs-right"></td>
            <td class="text-xs-right"></td>
            <td class="text-xs-right"></td>
            <td class="text-xs-left">{{ props.item.count_registrations }}</td>
            <td class="text-xs-right"></td>
            <td class="text-xs-right"></td>
            <td class="text-xs-right"></td>
            <td class="text-xs-left">{{ props.item.count_dancers }}</td>
            <td class="text-xs-right" v-if="$hasRole('admin')">{{ props.item.sum }}</td>
            <td class="text-xs-right" style="width: 125px;"></td>
          </tr>
          <tr v-show="props.item.expanded"
              v-for="registration in props.item.registrations"
              :key="registration.key"
              :class="{'stats': registration.stats_row, 'registration': !registration.stats_row}"
          >
            <td></td>
            <td class="">{{ registration.style }}</td>
            <td class="">{{ registration.sex ? $t(registration.sex) : '' }}</td>
            <td class="">{{ registration.age_group }}</td>
            <td class="">{{ registration.count_registrations }}</td>
            <td class="">{{ registration.name }}</td>
            <td class="">{{ registration.institution }}</td>
            <td class="" v-if="registration.media === true">
              <v-icon>done</v-icon>
            </td>
            <td class="" v-else-if="registration.media === false">
              <v-icon>clear</v-icon>
            </td>
            <td class="" v-else></td>
            <td class="">{{ registration.count_dancers }}</td>
            <td class="text-xs-right" v-if="$hasRole('admin')">{{ registration.sum }}</td>
            <td class="text-xs-right" style="width: 155px;">
              <v-tooltip top v-if="!registration.stats_row && $hasRole('admin')">
                <v-icon slot="activator" style="margin-bottom: -5px;" :color="registration.is_paid ? 'green' : 'grey'">
                  {{ registration.is_paid ? 'attach_money' : 'money_off' }}
                </v-icon>
                <span>{{ registration.is_paid ? $t('paid') : $t('unpaid') }}</span>
              </v-tooltip>

              <v-tooltip top v-if="!registration.stats_row && $hasRole('admin')">
                <v-btn icon
                       slot="activator"
                       @click="props.item.confirmed ? $router.push(`/admin/event-registration/${registration.id}`) : $router.push(`/admin/starter-registration/${registration.id}`)"
                       :loading="loading"
                       :disabled="loading"
                       class="mx-0">
                  <v-icon>edit</v-icon>
                </v-btn>
                <span>{{ $t('edit') }}</span>
              </v-tooltip>

              <v-tooltip top v-if="!registration.stats_row">
                <v-btn icon
                       slot="activator"
                       @click="detailsDialog(registration, props.item.confirmed)"
                       :loading="loading"
                       :disabled="loading"
                       class="mx-0">
                  <v-icon>info</v-icon>
                </v-btn>
                <span>{{ $t('details') }}</span>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
        v-model="statsDialog"
        fullscreen
        transition="dialog-bottom-transition"
        :hide-overlay="true"
        scrollable
    >
      <v-card tile>
        <v-toolbar card dark color="primary">
          <v-btn icon @click.native="statsDialog = false" dark>
            <v-icon>close</v-icon>
          </v-btn>
          <v-toolbar-title>Statistik</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark flat @click.native="downloadPDF">PDF</v-btn>
            <v-btn dark flat @click.native="downloadCSV">CSV</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-data-table
            :headers="statsTableHeaders"
            :items="statsItems"
            disable-initial-sort
            hide-actions
        >
          <template slot="items" slot-scope="props">
            <td>{{ props.item.company }}</td>
            <td>{{ props.item.first_name }}</td>
            <td>{{ props.item.last_name }}</td>
            <td>{{ props.item.dancers_count }}</td>
            <td>{{ props.item.deleted_dancers_count }}</td>
            <td>{{ props.item.supervisors_count }}</td>
            <td>{{ props.item.total_count }}</td>
            <td v-if="$hasRole('admin')">{{ props.item.sum_open }}</td>
            <td v-if="$hasRole('admin')">{{ props.item.sum }}</td>
          </template>
        </v-data-table>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import Vue from 'vue';
import jsPDF from 'jspdf';
import moment from "moment";

export default {
  data() {
    return {
      event: null,
      search: '',
      searchBuffer: '',
      loading: false,
      showOldEvents: false,
      loadingStats: false,
      loadingMails: false,
      loadingCSV: false,
      statsDialog: false,
      result: {
        groups: []
      },
      statsItems: [],
      timeout: null,
      loadingSearch: false
    }
  },
  watch: {
    searchBuffer() {
      this.loadingSearch = true;

      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        this.search = this.searchBuffer;
        this.loadingSearch = false;
      }, 500);
    }
  },
  computed: {
    eventItems() {
      if (this.$hasRole('admin')) {
        if (this.showOldEvents) {
          return this.events.items
        }

        return this.events.items.filter(e => {
          const endDate = moment(e.end)
          return endDate.diff(moment(), 'days') >= 0
        })
      }

      if (!this.user.is_organizer) {
        return null;
      }

      return _.filter(this.events.items, event => {
        return event.organizer_id === this.user.id;
      })
    },
    headers() {
      let headers = [
        {text: 'Wettbewerbs-Art', align: 'left', value: 'type_name', sortable: false},
        {text: 'Style', value: 'style', sortable: false},
        {text: 'Geschlecht', value: 'sex', sortable: false},
        {text: 'Altersklasse', value: 'age_group', sortable: false},
        {text: 'Starterzahl', value: 'count_registrations', sortable: false},
        {text: 'Startername', value: 'name', sortable: false},
        {text: 'Institution', value: 'institution', sortable: false},
        {text: 'Musik/Video', value: 'media', sortable: false},
        {text: 'Tänzerzahl', value: 'count_dancers', sortable: false}
      ];

      if (this.$hasRole('admin')) {
        headers.push({text: 'Gebühren', value: 'sum', align: 'right', sortable: false});
      }

      headers.push({text: '', sortable: false});

      return headers;
    },
    statsTableHeaders() {
      let headers = [
        {text: 'Firma', align: 'left', value: 'company', sortable: true},
        {text: 'Vorname', value: 'first_name', sortable: true},
        {text: 'Nachname', value: 'last_name', sortable: true},
        {text: 'Tänzer', value: 'dancers_count', sortable: true},
        {text: 'Stornierte Tänzer', value: 'deleted_dancers_count', sortable: true},
        {text: 'Betreuer', value: 'supervisors_count', sortable: true},
        {text: 'Gesamt', value: 'total_count', sortable: true}
      ];

      if (this.$hasRole('admin')) {
        headers.push({text: 'Gebühr offen', value: 'sum_open', sortable: true});
        headers.push({text: 'Gebühr gesamt', value: 'sum', sortable: true});
      }

      return headers;
    },
    eventNameWithDate() {
      return this.event !== null ? (this.$formatDate(this.event.start) + ' - ' + this.event.name) : null;
    },
    ...mapState(['user', 'events'])
  },
  created() {
    if (this.$route.params.id !== undefined) {
      _.each(this.events.items, event => {
        if (event.id === parseInt(this.$route.params.id)) {
          this.$set(this, 'event', event);
          return false;
        }
      });

      this.loadReport(parseInt(this.$route.params.id));
    } else {
      if (this.eventItems.length === 1) {
        this.$set(this, 'event', this.eventItems[0]);
        this.loadReport(this.event.id);
      }
    }
  },
  destroyed() {
    this.$setDrawer(null);
  },
  methods: {
    loadReport(id) {
      this.loading = true;
      axios.get(`/api/v1/event_reports/${id}`).then(response => {

        if (!response.data.success) {
          this.loading = false;
          this.$store.commit('snackBar', {
            color: 'error',
            text: 'Meldungen konnten nicht geladen werden'
          });
          return;
        }

        this.$set(this, 'result', response.data.data);
        // this.$set(this, 'resultCopy', _.cloneDeep(response.data.data.groups));

        this.loading = false;

        this.$router.push(`/event-reports/${id}`);
      }, errorResponse => {
        this.loading = false;
        this.$store.commit('snackBar', {
          color: 'error',
          text: 'Meldungen konnten nicht geladen werden'
        });
        console.error(errorResponse);
      });
    },
    detailsDialog(registration, confirmed) {
      let url = `/api/v1/event_registrations/${registration.id}`;
      if (!confirmed) {
        url = `/api/v1/starter_registrations/${registration.id}`;
      }
      axios.get(url).then(response => {
        if (!response.data.success) {
          return;
        }

        let registration = response.data.data;
        let user = registration.user;
        let members = confirmed ? registration.event_registration_members : registration.starter.starter_members;

        let infoLine = (key, value) => {
          return `<strong>${key}</strong><br>${value}<br>`
        };

        let html = '';

        if (user.display_name !== user.full_name) {
          html += infoLine('Institution', user.display_name);
        }

        html += infoLine('Name', user.full_name);

        if (user.postcode || user.town || user.full_street) {
          html += infoLine('Addresse', `${user.full_street}<br>${user.postcode} ${user.town}`);
        }

        if (user.telephone) {
          html += infoLine('Telefon', user.telephone);
        }

        html += infoLine('E-Mail', user.email);

        let copyText = '';
        _.each(members, member => {
          copyText += (copyText !== '' ? '\n' : '') + member.full_name;
        });

        let actions = [{
          label: 'Zu Institution wechseln',
          handler: () => {
            axios.get(`/api/v1/users/${registration.user.id}`).then(response => {
              if (!response.data.success) {
                return;
              }

              let user = response.data.data;

              this.$store.commit('switchUser', user);
              this.$store.commit('snackBar', {
                color: 'success',
                text: this.$t('user_switched', [`${user.first_name} ${user.last_name}`])
              });
              this.$router.push('/user/dashboard');

            }, errorResponse => {
              console.error(errorResponse);
            });
          }
        }]

        if (response.data.data.has_media) {
          actions.push({
            label: 'Medien-download',
            handler: () => {
              const user = JSON.parse(localStorage.getItem('user'))
              const win = window.open(`/meldeportal/api/v1/event_registrations/${registration.id}/media-download?api_token=${user.real_api_token ? user.real_api_token : user.api_token}`, '_blank')
              if (win) {
                win.focus()
              }
            }
          })
        }

        let name = confirmed ? registration.name : registration.starter.name;
        this.$store.commit('info', {
          headline: `Details zum Starter "${name}" von ${user.display_name}`,
          text: html,
          copyTitle: 'Teilnehmer des Starters',
          copyText: copyText,
          actions
        });
      });
    },
    mails(eventId) {
      if (this.loadingMails) {
        return;
      }

      this.loadingMails = true;

      axios.get(`/api/v1/events/${eventId}/mails`).then(response => {
        if (!response.data.success) {
          this.$store.commit('snackBar', {
            color: 'error',
            text: 'Mails konnten nicht abgerufen werden'
          });
          this.loadingMails = false;
          return;
        }

        window.location.href = 'mailto:?bcc=' + response.data.data.join(',');

        this.$store.commit('snackBar', {
          color: 'success',
          text: 'Mailprogramm wird geöffnet'
        });

        this.loadingMails = false;
      });
    },
    stats(eventId) {
      if (this.loadingStats) {
        return;
      }

      this.loadingStats = true;

      axios.get(`/api/v1/events/${eventId}/stats`).then(response => {
        if (!response.data.success) {
          this.$store.commit('snackBar', {
            color: 'error',
            text: 'Statistik konnte nicht abgerufen werden'
          });
          this.loadingStats = false;
          return;
        }

        Vue.set(this, 'statsItems', response.data.data);

        this.statsDialog = true;
        this.loadingStats = false;
      });
    },
    csv(eventId) {
      if (this.loadingCSV) {
        return;
      }

      this.loadingCSV = true;

      axios({
        url: `/api/v1/events/${eventId}/csv`,
        method: 'GET',
        responseType: 'blob'
      }).then(response => {
        let disposition = response.headers['content-disposition'];
        let filename = decodeURI(disposition.match(/filename="(.*)"/)[1]);

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        this.loadingCSV = false;
      });
    },
    getRow(item) {
      let row = [
        item.company === null ? '' : item.company,
        item.first_name,
        item.last_name,
        item.dancers_count,
        item.deleted_dancers_count,
        item.supervisors_count,
        item.total_count
      ];
      if (this.$hasRole('admin')) {
        row.push(this.$currency(item.sum_open_int, true));
        row.push(this.$currency(item.sum_int, true));
      }
      return row;
    },
    getColumns() {
      let cols = ['Firma', 'Vorname', 'Nachname', 'Tänzer', 'Stornierte Tänzer', 'Betreuer', 'Gesamt'];
      if (this.$hasRole('admin')) {
        cols.push('Gebühr offen');
        cols.push('Gebühr gesamt');
      }
      return cols;
    },
    downloadPDF() {
      let columns = this.getColumns();
      let rows = [];

      let items = _.orderBy(this.statsItems, ['company', 'first_name', 'last_name'], ['asc', 'asc', 'asc']);
      _.forEach(items, item => {
        rows.push(this.getRow(item))
      });

      let doc = new jsPDF('l', 'pt');

      doc.autoTable({
        head: [columns],
        body: rows,
        styles: {
          fontSize: 10,
          overflow: 'linebreak'
        }
      });

      doc.save(`Statistik_${this.event.name}.pdf`)
    },
    downloadCSV() {
      let csvContent = "data:text/csv;charset=utf-8,\uFEFF";
      csvContent += this.getColumns().join(";") + "\r\n";
      _.forEach(this.statsItems, (item) => {
        let row = this.getRow(item).join(";");
        csvContent += row + "\r\n";
      });

      let encodedUri = encodeURI(csvContent);
      let link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Statistik_${this.event.name}.csv`);
      document.body.appendChild(link); // Required for FF

      link.click();
    },
    filter(items, search, filter) {
      if (search === '') {
        return items;
      }

      search = search.toLowerCase();

      let result = [];

      _.each(items, i => {
        let registrations = _.filter(i.registrations, r => {
          if (r.stats_row) {
            return true;
          }

          return filter(r.name, search) || filter(r.institution, search) || filter(r.sum, search) || filter(r.style, search);
        });

        if (registrations.length > 0 && !_.every(registrations, r => r.stats_row)) {
          let copy = _.cloneDeep(i);
          copy.expanded = true;
          copy.registrations = registrations;
          result.push(copy);
        }
      });

      return result;
    }
  }
}
</script>

<style>
#event-report-table .expandable {
  cursor: pointer;
}

#event-report-table tr.registration {
  background: #fafafa;
}

.event-report-toolbar .v-input__slot {
  margin-bottom: 2px !important;
}
</style>
